import { useMemo, useState } from "react";
import { FileText, UserPlus, X } from "lucide-react";
import { Alert, Button, Card, Checkbox, Col, Flex, Form, Input, InputNumber, Radio, Row, Select, Space, Upload, theme } from "antd";
import { StudentActions } from "./StudentActions.jsx";
import { matchesSchoolSearch } from "../../data/schools.js";
import { GRADE_OPTIONS, SCHOOL_LEVELS, getCourseLabel, getInitialGrade } from "../../utils/academy.js";
import { SchoolCreateModal } from "../academy/SchoolCreateModal.jsx";
import { formatStudentContact } from "../../utils/studentContact.js";
import { studentSchoolName, studentSchoolOptions } from "../../utils/studentSchool.js";
import { CourseSetupModal } from "./CourseSetupModal.jsx";

const { TextArea } = Input;
const studentStatusOptions = [
  { value: "재원", label: "재원" },
  { value: "휴원", label: "휴원" },
  { value: "퇴원", label: "퇴원" },
  { value: "졸업", label: "졸업" }
];
const billingMethodOptions = ["현장 결제", "모바일 청구서", "매번 선택"];

function formatPhoneNumber(value) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function StudentEditor({
  editingId,
  form,
  setForm,
  onSubmit,
  onCancel,
  billingVisible = true,
  academyInfo = {},
  canManageSchools = false,
  refreshSchools,
  courses = [], subjects = [], teachers = [], saving = false
}) {
  const [addingSchool, setAddingSchool] = useState(false);
  const { token } = theme.useToken();
  const selectedCourses = form.registrationEnrollments || [];
  const [setupCourse, setSetupCourse] = useState(null);
  function commitSetup(values, setupStatus) {
    setForm(current => ({ ...current, registrationEnrollments: [
      ...(current.registrationEnrollments || []).filter(item => item.courseGroupId !== setupCourse.courseGroupId),
      { ...setupCourse, ...values, setupStatus }
    ] }));
    setSetupCourse(null);
  }
  function chooseCourse(id) {
    setSetupCourse(selectedCourses.find(item => item.courseGroupId === id) || {
      courseGroupId: id, endDate: '', endReason: '', startDate: '',
      appliedTuition: courses.find(course => course.id === id)?.tuition ?? '', firstMonthAdjustment: null, memo: ''
    });
  }
  function selectCourses(ids) {
    setForm(current => ({ ...current, registrationEnrollments: ids.map(id =>
      (current.registrationEnrollments || []).find(item => item.courseGroupId === id) || {
        courseGroupId: id, endDate: '', endReason: '',
        appliedTuition: courses.find(course => course.id === id)?.tuition ?? '', firstMonthAdjustment: '', memo: ''
      }) }));
  }
  const gradeOptions = GRADE_OPTIONS[form.schoolLevel] || [];
  const schoolOptions = useMemo(
    () => {
      const options = studentSchoolOptions(academyInfo, form.schoolLevel);
      // Editing legacy/inactive schools must not silently replace saved names.
      if (editingId && form.school && !options.some((option) => option.value === form.school)) {
        options.unshift({ value: form.school, label: studentSchoolName(form.school, academyInfo) });
      }
      return options;
    },
    [academyInfo, form.schoolLevel, form.school, editingId]
  );
  function updateSchoolLevel(schoolLevel) {
    setForm({ ...form, schoolLevel, school: "", grade: getInitialGrade(schoolLevel), schoolConfirmationRequired: false, graduationReviewRequired: false });
  }

  function updateGrade(grade) {
    setForm({ ...form, grade });
  }
  async function schoolSaved(school) {
    await refreshSchools();
    const value = `${school.name} (${school.city} ${school.district})`;
    setForm((current) => ({ ...current, school: value, schoolConfirmationRequired: false }));
    setAddingSchool(false);
  }

  return (
    <Form className="student-editor-form student-management-page" layout="vertical" size="medium" disabled={saving} onFinish={onSubmit} initialValues={form}>
      <p className="student-editor-required-note"><span>*</span> 이름, 과정, 학년과 학교는 필수 입력 항목입니다.</p>

      <section className="student-editor-section">
        <div className="student-editor-section-heading">
          <div><h3>학생 기본 정보</h3><p>이름과 재원 기준이 되는 학교 정보를 먼저 입력합니다.</p></div>
          <span>필수 정보</span>
        </div>
        <Row gutter={[12, 16]}>
          <Col xs={24} md={12}>
            <Form.Item label="이름" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="학생 이름" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="학원 등록일">
              <Input disabled={saving || Boolean(editingId)} type="date" value={form.joinedAt} onChange={(e) => setForm({ ...form, joinedAt: e.target.value })} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="과정" required>
              <Select value={form.schoolLevel || undefined} onChange={updateSchoolLevel} options={SCHOOL_LEVELS.map((value) => ({ value, label: value }))} placeholder="선택" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="학년" required>
              <Select value={form.grade || undefined} onChange={updateGrade} disabled={saving || !gradeOptions.length} options={gradeOptions.map((grade) => ({ value: grade, label: `${grade}학년` }))} placeholder={form.schoolLevel === "재수" ? "선택 없음" : "선택"} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="학교" required={!form.schoolConfirmationRequired && form.schoolLevel !== "재수"} extra={form.schoolConfirmationRequired ? "진학 학교를 확인한 뒤 선택하세요." : null}>
              <Select allowClear showSearch value={form.school || undefined} onChange={(school) => setForm({ ...form, school: school || "", schoolConfirmationRequired: school ? false : form.schoolConfirmationRequired })} options={schoolOptions} filterOption={matchesSchoolSearch} placeholder={form.schoolLevel ? "학교 검색" : "과정을 먼저 선택"} disabled={saving || !form.schoolLevel || form.schoolLevel === "재수"} />
              {canManageSchools && refreshSchools && <Button type="link" disabled={saving || !form.schoolLevel || form.schoolLevel === "재수"} onClick={() => setAddingSchool(true)}>목록에 없는 학교 추가</Button>}
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="상태">
              <Radio.Group block options={studentStatusOptions} value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })} optionType="button" buttonStyle="solid" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="성별">
              <Select value={form.gender || undefined} onChange={(gender) => setForm({ ...form, gender })} allowClear options={["남", "여"].map((value) => ({ value, label: value }))} placeholder="선택 안함" />
            </Form.Item>
          </Col>
        </Row>
      </section>

      <section className="student-editor-section">
        <div className="student-editor-section-heading"><div><h3>연락처 및 납부</h3><p>학생과 보호자의 연락처, 기본 납부 방식을 입력합니다.</p></div></div>
        <Row gutter={[12, 16]}>
          <Col xs={24} md={12}>
            <Form.Item label="학생 연락처">
              <Input value={formatStudentContact(form.phone)} onChange={(e) => setForm({ ...form, phone: formatPhoneNumber(e.target.value) })} inputMode="tel" maxLength={13} placeholder="000-0000-0000" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="보호자 관계" required={Boolean(form.parentPhone)}>
              <Space>
                {["부", "모"].map((relationship) => (
                  <Checkbox key={relationship} checked={form.guardianRelationship === relationship} disabled={saving || !form.parentPhone} onChange={(event) => setForm({ ...form, guardianRelationship: event.target.checked ? relationship : "" })}>{relationship}</Checkbox>
                ))}
              </Space>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="보호자 연락처">
              <Input value={formatStudentContact(form.parentPhone)} onChange={(e) => setForm({ ...form, parentPhone: formatPhoneNumber(e.target.value) })} inputMode="tel" maxLength={13} placeholder="000-0000-0000" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="주 납부 방식">
              <Select disabled={saving || !billingVisible} value={form.billingMethod || undefined} onChange={(billingMethod) => setForm({ ...form, billingMethod })} options={billingMethodOptions.map((value) => ({ value, label: value }))} placeholder="매번 선택" />
            </Form.Item>
          </Col>
        </Row>
      </section>

      {!editingId && <section className="student-editor-section">
        <div className="student-editor-section-heading"><div><h3>수업 등록</h3><p>수업은 지금 선택하거나 학생 등록 후에 추가할 수 있습니다.</p></div><span>선택 입력</span></div>
        {!billingVisible ? <Alert type="info" showIcon message="수업 배정은 원장·관리자가 할 수 있습니다." /> : <>
          <Form.Item className="student-registration-field">
            <Select showSearch optionFilterProp="label" placeholder="수업 선택 (선택 사항)"
              aria-label="수업 선택"
              notFoundContent={courses.some(course => course.active !== false) ? "선택할 수업이 없습니다." : "등록된 수업이 없습니다."}
              className="student-registration-select" value={null} disabled={saving || selectedCourses.length >= 20}
              onChange={chooseCourse}
              options={courses.filter(course => course.active !== false && !selectedCourses.some(item => item.courseGroupId === course.id)).map(course => ({value: course.id, label: getCourseLabel(course, subjects, teachers)}))} />
          </Form.Item>
          {selectedCourses.length > 0 && <Space className="student-registration-courses" direction="vertical" size={12} aria-label="선택한 수업">
            {selectedCourses.map(item => {
              const course = courses.find(course => course.id === item.courseGroupId) || {};
              return <Card key={item.courseGroupId} className="student-registration-course" size="small"
                style={{background: token.colorPrimaryBg, borderColor: token.colorPrimaryBorder, boxShadow: 'none'}}
                styles={{body: {padding: '8px 12px'}}}>
                <Flex align="center" justify="space-between" gap={12}>
                  <div className="student-registration-course-label">
                    <Button type="link" onClick={() => chooseCourse(item.courseGroupId)} disabled={saving}>{getCourseLabel(course, subjects, teachers)}</Button>
                    <div className="student-registration-setup-summary">
                      <span>첫 수업 시작일: {item.setupStatus === 'confirmed' ? item.startDate : '-'}</span>
                      <span>첫 달 조정 금액: {item.setupStatus === 'confirmed' ? `${Number(item.firstMonthAdjustment).toLocaleString('ko-KR')}원` : '-'}</span>
                    </div>
                  </div>
                  <Button className="student-registration-remove" type="text" size="small" icon={<X size={14} />} disabled={saving}
                    aria-label={`${course.className || '선택한 수업'} 수업 선택 해제`}
                    onClick={() => selectCourses(selectedCourses.filter(selected => selected.courseGroupId !== item.courseGroupId).map(selected => selected.courseGroupId))} />
                </Flex>
              </Card>;
            })}
          </Space>}
          <CourseSetupModal open={Boolean(setupCourse)} title={getCourseLabel(courses.find(course => course.id === setupCourse?.courseGroupId) || {}, subjects, teachers)}
            initial={setupCourse} saving={saving} onClose={() => setSetupCourse(null)}
            onLater={() => commitSetup({ startDate: '', firstMonthAdjustment: null }, 'pending')}
            onSave={values => commitSetup(values, 'confirmed')} />
        </>}
      </section>}

      <section className="student-editor-section">
        <div className="student-editor-section-heading"><div><h3>관리 메모</h3><p>상담이나 수업 배정 시 참고할 내용을 남깁니다.</p></div><span>선택 입력</span></div>
        <Form.Item label="학생 메모">
          <TextArea value={form.memo || ""} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={4} placeholder="학생 관리에 필요한 내용을 입력하세요." />
        </Form.Item>
      </section>

      <StudentActions className="student-editor-actions">
          <Button disabled={saving} onClick={onCancel}>취소</Button>
          <Button type="primary" loading={saving} htmlType="submit" icon={<UserPlus size={16} />}>{editingId ? "저장" : "등록"}</Button>
      </StudentActions>
      {canManageSchools && <SchoolCreateModal className="student-management-modal" open={addingSchool} schoolLevel={form.schoolLevel} title="목록에 없는 학교 추가" onCancel={() => setAddingSchool(false)} onSaved={schoolSaved} />}
    </Form>
  );
}

export function ScoreEditor({ scoreForm, setScoreForm, onSubmit, formId, fileList = [], setFileList = () => {} }) {
  return (
    <Form id={formId} className="student-record-form" layout="vertical" size="medium" onFinish={onSubmit}>
      <Row gutter={[12, 16]}>
        <Col xs={24} md={12}>
          <Form.Item label="시험 이름" required>
            <Input value={scoreForm.title} onChange={(e) => setScoreForm({ ...scoreForm, title: e.target.value })} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="과목" required>
            <Input value={scoreForm.subject} onChange={(e) => setScoreForm({ ...scoreForm, subject: e.target.value })} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="시험일">
            <Input type="date" value={scoreForm.date} onChange={(e) => setScoreForm({ ...scoreForm, date: e.target.value })} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="레벨">
            <Select value={scoreForm.level} onChange={(level) => setScoreForm({ ...scoreForm, level })} options={["상", "중", "하"].map((value) => ({ value, label: value }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="점수" required>
            <InputNumber min={0} value={scoreForm.score === "" ? null : Number(scoreForm.score)} onChange={(score) => setScoreForm({ ...scoreForm, score: score === null ? "" : String(score) })} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="만점">
            <InputNumber min={0} value={Number(scoreForm.maxScore || 100)} onChange={(maxScore) => setScoreForm({ ...scoreForm, maxScore: maxScore || 100 })} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="코멘트">
            <TextArea value={scoreForm.comment} onChange={(e) => setScoreForm({ ...scoreForm, comment: e.target.value })} rows={4} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="시험지 스캔본">
            <Upload disabled title="성적별 첨부파일 연결 계약 확인이 필요합니다." multiple beforeUpload={() => false} fileList={fileList} onChange={({ fileList: nextList }) => setFileList(nextList)} accept=".jpg,.jpeg,.png,.pdf">
              <Button icon={<FileText size={16} />}>파일 선택</Button>
            </Upload>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
